import React from 'react';
import styled from 'styled-components';
import { BriefEntry_UserFragment } from '@99designs/apps/spa/pages/create/briefs/BriefEntry.generated';
import BriefDetailsList from '@99designs/apps/spa/pages/create/components/BriefDetailsList';
import { BriefDetails_StructuredBriefFragment } from '@99designs/apps/spa/pages/create/components/BriefDetailsList.generated';
import BriefEntryAction from '@99designs/apps/spa/pages/create/components/BriefEntryAction';
import ContentSetting, { Sizes } from '@99designs/apps/spa/pages/create/components/ContentSetting';
import Header from '@99designs/apps/spa/pages/create/components/Header';
import HeaderUserLayout from '@99designs/apps/spa/pages/create/components/HeaderUserLayout';
import { trackBriefEntryViewedEvent } from '@99designs/apps/spa/pages/create/events';
import continueBrief from '@99designs/apps/spa/pages/create/images/create/continue_brief.svg';
import copyBrief from '@99designs/apps/spa/pages/create/images/create/copy_brief.svg';
import newBrief from '@99designs/apps/spa/pages/create/images/create/new_brief.svg';
import { Card } from '@99designs/brief';
import { scrollToElement } from '@99designs/browser';
import { Matrix, MatrixItem } from '@99designs/emu';
import { Desktop } from '@99designs/frontend-components/components/Desktop';
import { Icon } from '@99designs/frontend-components/components/Icon';
import { formatMessage } from '@99designs/frontend-components/i18n';

export interface Props {
    designer: { displayName: string; id: string; profile: { certifications: string[] } } | null;
    recentDrafts: BriefDetails_StructuredBriefFragment[];
    recentLaunchedBriefs: BriefDetails_StructuredBriefFragment[];
    createBriefAction: () => void;
    resumeBriefAction: (brief: { id: string }) => void;
    copyBriefAction: (brief: { id: string }) => void;
    user: BriefEntry_UserFragment;
}

export enum Selection {
    CREATE = 'create',
    RESUME = 'resume',
    COPY = 'copy',
}

interface State {
    currentSelection: Selection | null;
}

const Designer = styled.div`
    display: flex;
    justify-content: center;
`;

class BriefEntryPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            currentSelection: null,
        };
    }

    componentDidMount() {
        trackBriefEntryViewedEvent({ user_id: parseInt(this.props.user.id) });
    }

    toggleDisplayBriefs = (mode: Selection) => {
        if (mode !== this.state.currentSelection) {
            this.setState({
                currentSelection: mode,
            });
        } else {
            this.setState({
                currentSelection: null,
            });
        }
    };

    render() {
        const {
            designer,
            user,
            createBriefAction,
            recentDrafts,
            resumeBriefAction,
            recentLaunchedBriefs,
            copyBriefAction,
        } = this.props;

        const contentSettingSize = !(recentDrafts.length && recentLaunchedBriefs.length)
            ? Sizes.Small
            : undefined;
        const matrixSize = recentDrafts.length && recentLaunchedBriefs.length ? 3 : 2;

        return (
            <ContentSetting size={contentSettingSize}>
                <Header title={__("Hey, let's get started")} textAlignment={'center'}>
                    <h2 className="heading heading--h2">
                        {formatMessage(__("Hey {displayName}, let's get started"), {
                            displayName: user.displayName,
                        })}
                    </h2>
                    {designer && (
                        <Designer>
                            <HeaderUserLayout
                                users={[{ id: designer.id, displayName: designer.displayName }]}
                            />
                        </Designer>
                    )}
                </Header>

                <div className="brief-actions-layout">
                    <Matrix columns={[1, matrixSize]} space="2bu">
                        {recentLaunchedBriefs.length > 0 && (
                            <MatrixItem>
                                <Card
                                    onClick={() => this.toggleDisplayBriefs(Selection.COPY)}
                                    variations={['clickable', 'hoverable'].concat(
                                        // eslint-disable-next-line eqeqeq
                                        this.state.currentSelection == Selection.COPY
                                            ? ['selected']
                                            : []
                                    )}
                                >
                                    <BriefEntryAction
                                        svg={copyBrief}
                                        title={__('Copy a previous project')}
                                    />
                                    {/* eslint-disable-next-line eqeqeq */}
                                    {this.state.currentSelection == Selection.COPY && (
                                        <Desktop>
                                            <Card.Icon>
                                                <Icon type={'tick-3'} variations={['3x']} />
                                            </Card.Icon>
                                        </Desktop>
                                    )}
                                </Card>
                            </MatrixItem>
                        )}
                        {recentDrafts.length > 0 && (
                            <MatrixItem>
                                <Card
                                    onClick={() => this.toggleDisplayBriefs(Selection.RESUME)}
                                    variations={['clickable', 'hoverable'].concat(
                                        // eslint-disable-next-line eqeqeq
                                        this.state.currentSelection == Selection.RESUME
                                            ? ['selected']
                                            : []
                                    )}
                                    data-testid="continue-brief"
                                >
                                    <BriefEntryAction
                                        svg={continueBrief}
                                        title={__('Continue with your draft')}
                                    />
                                    {/* eslint-disable-next-line eqeqeq */}
                                    {this.state.currentSelection == Selection.RESUME && (
                                        <Desktop>
                                            <Card.Icon>
                                                <Icon type={'tick-3'} variations={['3x']} />
                                            </Card.Icon>
                                        </Desktop>
                                    )}
                                </Card>
                            </MatrixItem>
                        )}
                        <MatrixItem>
                            <Card
                                onClick={() => {
                                    this.toggleDisplayBriefs(Selection.CREATE);
                                    createBriefAction();
                                }}
                                variations={['clickable', 'hoverable']}
                                data-testid="start-brief"
                            >
                                <BriefEntryAction svg={newBrief} title={__('Start a new brief')} />
                            </Card>
                        </MatrixItem>
                    </Matrix>
                </div>
                <div ref={(ref) => ref && scrollToElement(ref, 15)}>
                    {this.state.currentSelection === Selection.RESUME && (
                        <Card>
                            <BriefDetailsList
                                drafts={recentDrafts}
                                onClick={resumeBriefAction}
                                heading={__('Choose one of your most recent drafts')}
                                designer={designer}
                            />
                        </Card>
                    )}
                    {this.state.currentSelection === Selection.COPY && (
                        <Card>
                            <BriefDetailsList
                                drafts={recentLaunchedBriefs}
                                onClick={copyBriefAction}
                                heading={__('Choose one of your most recent projects')}
                                designer={designer}
                            />
                        </Card>
                    )}
                </div>
            </ContentSetting>
        );
    }
}

export default BriefEntryPage;
